import { Add, Delete, Edit } from '@mui/icons-material';
import { Button, Checkbox, FormControlLabel, Grid, useMediaQuery } from '@mui/material';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router';
import { Redirect } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import {
  Button as SMButton,
  Loader,
  OrderManagementPage,
  PageTitle,
  Table
} from '../../../components';
import { UserContext } from '../../../context';
import { deleteAdminRole, getAdminRoles } from '../../../fetch';
import { formatMoney, isAllowedToBeEditedOrDeleted } from '../../../helpers';
import { IRole } from '../../../models';
import { ManagePartsModal } from './MangePartsModal';
import { MobileManageRoles } from './MobileManageRoles';
import { useFlags } from '../../../hooks';

export const ManageRoles = () => {
  const { user, canEdit, isBillingAdmin, isFinancialAdmin, isGlobalAdmin } =
    useContext(UserContext);
  const { enqueueSnackbar } = useSnackbar();
  const [roles, setRoles] = useState<IRole[]>([]);
  const [isDeleting, setDeleting] = useState<boolean>(false);
  const [isLoadingRoles, setLoadingRoles] = useState<boolean>(false);
  const [selectedRoles, setSelectedRoles] = useState<any[]>([]);
  const [managePartsModalIsOpen, setManagePartsModalIsOpen] = useState<boolean>(false);

  const classes = useStyles();
  const isSmMobile = useMediaQuery('(max-width: 600px)');
  const history = useHistory();
  const featureFlags = useFlags();

  const fetchRoles = async () => {
    try {
      setLoadingRoles(true);
      const res = await getAdminRoles();
      setRoles(res.records);
    } catch (error) {
      enqueueSnackbar('Error loading roles. Please refresh and try again.', { variant: 'error' });
    } finally {
      setLoadingRoles(false);
    }
  };

  useEffect(() => {
    fetchRoles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDelete = async (id: number) => {
    const result = window.confirm('Are you sure you want to delete this role?');
    if (result) {
      setDeleting(true);
      try {
        await deleteAdminRole(id);
        enqueueSnackbar('Role deleted successfully!', { variant: 'success' });
        fetchRoles();
      } catch (error) {
        const msg = error?.response?.data?.Detail || 'Unable to delete role.';
        enqueueSnackbar(msg, { variant: 'error' });
      } finally {
        setDeleting(false);
      }
    }
  };

  const columns = useMemo(() => {
    return [
      {
        id: 'checkbox',
        accessor: '',
        Header: '',
        sort: false,
        overrideWidth: 100,
        Cell: ({ cell }) => {
          const role = cell.row.original;
          const ids = selectedRoles.map(role => role.placeOrderRoleId);
          const isSelected = ids.includes(role.placeOrderRoleId);
          const showCheckbox = canEdit && isAllowedToBeEditedOrDeleted(cell.row.original.name);
          return (
            showCheckbox && (
              <FormControlLabel
                classes={{
                  label: classes.tableCheckbox
                }}
                color="primary"
                control={
                  <Checkbox
                    checked={isSelected}
                    id="roles-checkbox"
                    onChange={() => {
                      isSelected
                        ? setSelectedRoles(
                            selectedRoles.filter(
                              selectedRole =>
                                selectedRole.placeOrderRoleId !== role.placeOrderRoleId
                            )
                          )
                        : setSelectedRoles([...selectedRoles, role]);
                    }}
                  />
                }
                label=""
              />
            )
          );
        }
      },
      {
        Header: 'Role',
        accessor: 'name',
        sort: false
      },
      {
        Header: 'Materials Spend Limit',
        accessor: 'spendLimit',
        sort: false,
        Cell: ({ cell }) => {
          return (
            isAllowedToBeEditedOrDeleted(cell.row.original.name) && (
              <>{formatMoney(cell.row.original.spendLimit, 0)}</>
            )
          );
        }
      },
      {
        Header: 'Change Order Spend Limit',
        accessor: 'spendLimitChangeOrder',
        sort: false,
        Cell: ({ cell }) => {
          return (
            isAllowedToBeEditedOrDeleted(cell.row.original.name) && (
              <>{formatMoney(cell.row.original.spendLimitChangeOrder, 0)}</>
            )
          );
        }
      },
      {
        id: 'actions',
        Header: '',
        accessor: '',
        sort: false,
        overrideWidth: isGlobalAdmin ? 200 : 100,
        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: IRole } };
        }) => {
          return (
            canEdit &&
            isAllowedToBeEditedOrDeleted(original.name) && (
              <div className={classes.actionButtons}>
                <Button
                  color="primary"
                  startIcon={<Edit />}
                  className={classes.editButton}
                  onClick={async () => {
                    history.push(`/production/orders/manage-roles/${original.placeOrderRoleId}`);
                  }}
                >
                  Edit
                </Button>

                {isGlobalAdmin && (
                  <Button
                    startIcon={<Delete />}
                    className={classes.deleteButton}
                    onClick={() => {
                      handleDelete(original.placeOrderRoleId);
                    }}
                  >
                    Delete
                  </Button>
                )}
              </div>
            )
          );
        }
      }
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roles, selectedRoles]);

  if (!featureFlags.authorizeUserNavigationAccess) {
    if (
      user &&
      user.userRoles &&
      !user.userRoles.includes('BL_Admin') &&
      !isBillingAdmin &&
      !isFinancialAdmin
    ) {
      return <Redirect to={'/production/orders/order-management'} />;
    }
  }

  return (
    <OrderManagementPage title="Manage Roles">
      {isDeleting && (
        <Loader position="centered" type="fullscreen">
          Deleting...
        </Loader>
      )}
      <PageTitle title="Manage Roles" />
      <Grid
        container
        alignItems="center"
        justifyContent={isSmMobile ? 'center' : 'space-between'}
        className={classes.addRoleWrapper}
      >
        {canEdit && (
          <SMButton
            id="edit-roles"
            startIcon={<Edit />}
            disabled={selectedRoles.length <= 0}
            onClick={() => {
              setManagePartsModalIsOpen(true);
            }}
            color="primary"
            variant="contained"
            className={classes.editRoleButton}
          >
            Edit
          </SMButton>
        )}
        {isGlobalAdmin && (
          <SMButton
            id="create-user"
            startIcon={<Add />}
            onClick={() => history.push('/production/orders/manage-roles/add')}
            color="primary"
            variant="contained"
            className={classes.addRoleButton}
          >
            Add Role
          </SMButton>
        )}
      </Grid>
      <Table
        columns={columns}
        data={roles}
        isLoading={isLoadingRoles}
        hidePagination
        ResponsiveComponent={isSmMobile ? MobileManageRoles : undefined}
        rowClasses={row => {
          if (row.name.includes('Admin') || row.name === 'Content Manager') {
            return classes.masterRow;
          }
        }}
        mobileProps={{
          handleDelete: isGlobalAdmin ? role => handleDelete(role.placeOrderRoleId) : null,
          handleEdit: canEdit
            ? (val: IRole) =>
                history.push(`/production/orders/manage-roles/${val.placeOrderRoleId}`)
            : null
        }}
      />
      <ManagePartsModal
        open={managePartsModalIsOpen}
        onClose={() => setManagePartsModalIsOpen(false)}
        roles={selectedRoles}
      />
    </OrderManagementPage>
  );
};

const useStyles = makeStyles((theme: Theme) => ({
  addRoleWrapper: {
    marginBottom: theme.spacing(1)
  },
  addRoleButton: {
    marginTop: theme.spacing(1),
    width: '100%',
    [theme.breakpoints.up('sm')]: {
      width: 'auto'
    }
  },
  editRoleButton: {
    alignSelf: 'flex-start',
    marginTop: theme.spacing(1),
    width: '100%',
    [theme.breakpoints.up('sm')]: {
      width: 'auto'
    }
  },
  actionButtons: {
    display: 'flex'
  },
  editButton: {},
  deleteButton: {
    color: theme.palette.error.main
  },
  tableCheckbox: {
    marginRight: 0
  },
  masterRow: {
    background: theme.palette.grey[200]
  }
}));
